/* Circle D — 2026 landing page behaviour.
   Vanilla port of the Claude Design component logic (Homepage.dc.html).
   The design ran inside the DC runtime, which supplied React refs, state and
   prop tokens. None of that exists on the storefront, so each behaviour is
   rebound here to markup hooks: refs become ids, onClick/onChange become
   delegated listeners, and the prop tokens are read off data-* attributes
   that the section writes from its schema settings. */
(function () {
  'use strict';

  /* ---- Find My Fit — real, tag-backed routing -------------------------
     Every option below corresponds to a tag that actually exists on products,
     and every handle matches one the "Shop your build" make tiles already link
     to — so the URLs are proven, not guessed. The previous list was written
     from memory and offered combinations the catalog has never carried (C6,
     727 TorqueFlite, 68RFE), which is part of why the button was never wired
     up: there was nowhere valid to send them.

     `tag` is the Shopify-handleized product tag. Collection + tag URLs stack
     with "+", so make/trans+goal narrows correctly:
         /collections/gm/10l90+goal-drag-race */
  var MAKES = {
    GM:    { handle: 'gm', trans: [
      { label: '4L60E · LS',            tag: '4l60-65-70-ls' },
      { label: '4L60E · LT',            tag: '4l60-lt' },
      { label: '4L80E',                  tag: '4l80' },
      { label: '6L80 / 6L90',            tag: '6l80-90' },
      { label: '8L90',                   tag: '8l90' },
      { label: '10L90',                  tag: '10l90' },
      { label: 'Powerglide / TH350 / TH400', tag: 'pg-350-400' }
    ]},
    Ford:  { handle: 'ford', trans: [
      { label: '4R70W',                  tag: '4r70' },
      { label: '4R100 / E4OD',           tag: '4r100-e40d' },
      { label: '5R55',                   tag: '5r55' },
      { label: '6R80',                   tag: '6r80' },
      { label: '10R60',                  tag: '10r60' },
      { label: '10R80',                  tag: '10r80' }
    ]},
    Mopar: { handle: 'mopar', trans: [
      { label: '8HP70',                  tag: '8hp70' },
      { label: '8HP90',                  tag: '8hp90' },
      { label: 'NAG1 · WA580',          tag: 'nag1' },
      { label: '545RFE',                 tag: '545rfe' },
      { label: '65RFE',                  tag: '65rfe' }
    ]}
  };

  var TAG = {
    daily: 'goal-street-daily',
    strip: 'goal-street-strip',
    race:  'goal-drag-race',
    tow:   'goal-towing-truck'
  };
  var GOALS = {
    'Street / Daily': 'daily',
    'Street + Strip': 'strip',
    'Drag / Race':    'race',
    'Towing / Truck': 'tow'
  };

  /* Which goals actually return products for a given transmission, derived
     from the catalog on 2026-08-09 when the goal tags were applied.

     This exists because the goal bands overlap by diameter but the catalog is
     not uniform: an 8HP is only ever a 10" (race), a NAG1 only an 11" (street),
     and no 10-speed is a towing converter by definition. Without this the CTA
     happily builds /collections/ford/10r80+goal-towing-truck — a URL that
     resolves 200 OK to an empty grid, which is the same dead end in a new
     costume. Impossible combinations go to the specialist form instead, which
     is what the off-ramp directly beneath this section already offers.

     If the catalog gains or loses converters, regenerate this map. */
  var VALID = {
    '4l60-65-70-ls': ['race', 'strip', 'daily'],
    '4l60-lt':       ['race', 'strip', 'daily'],
    '4l80':          ['race', 'strip', 'daily', 'tow'],
    '6l80-90':       ['race', 'strip', 'daily', 'tow'],
    '8l90':          ['race', 'strip', 'daily'],
    '10l90':         ['race', 'strip', 'daily'],
    'pg-350-400':    ['race', 'strip', 'daily'],
    '4r70':          ['race', 'strip', 'daily'],
    '4r100-e40d':    ['race', 'strip', 'daily', 'tow'],
    '5r55':          ['race', 'strip'],
    '6r80':          ['race', 'strip', 'daily'],
    '10r60':         ['race', 'strip', 'daily'],
    '10r80':         ['race', 'strip', 'daily'],
    '8hp70':         ['race'],
    '8hp90':         ['race'],
    'nag1':          ['daily'],
    '545rfe':        ['race', 'strip', 'daily', 'tow'],
    '65rfe':         ['daily', 'tow']
  };

  /* Nothing selected yet -> the old behaviour (jump to the lineup). A make with
     no transmission -> that make's collection. Anything more specific stacks. */
  var FALLBACK = '#showcase';
  var ASK = '/pages/ask-circled';

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else { fn(); }
  }

  ready(function () {
    var root = document.querySelector('[data-cds-home]');
    if (!root) return;

    /* ---- design tokens from section settings ---------------------------
       applyTokens() in the original wrote these onto documentElement from
       props. The section renders them as data-* so the same knobs survive
       into the theme editor. */
    var rs = document.documentElement.style;
    var scrollLen = parseInt(root.getAttribute('data-anatomy-scroll'), 10);
    if (scrollLen) rs.setProperty('--anatomy-h', scrollLen + 'vh');
    var accent = root.getAttribute('data-accent');
    if (accent) rs.setProperty('--ignite', accent);

    /* ---- sticky nav ---- */
    var nav = document.querySelector('header.nav');
    if (nav) {
      var onScroll = function () {
        nav.classList.toggle('scrolled', window.scrollY > 30);
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
    }

    /* ---- staggered reveals ----
       Same stagger maths as the original: index among not-yet-revealed
       siblings, capped at 6, times 80ms. */
    var reveals = [].slice.call(document.querySelectorAll('.reveal'));
    if (reveals.length) {
      if ('IntersectionObserver' in window) {
        var io = new IntersectionObserver(function (entries) {
          entries.forEach(function (e) {
            if (!e.isIntersecting) return;
            var sibs = [].slice.call(e.target.parentElement.children)
              .filter(function (c) {
                return c.classList.contains('reveal') && !c.classList.contains('in');
              });
            var i = Math.min(Math.max(sibs.indexOf(e.target), 0), 6);
            e.target.style.transitionDelay = (i * 80) + 'ms';
            e.target.classList.add('in');
            io.unobserve(e.target);
          });
        }, { threshold: 0.16 });
        reveals.forEach(function (el) { io.observe(el); });
      }
      /* fail-safe: the observer is a nicety, not a gate */
      setTimeout(function () {
        document.querySelectorAll('.reveal:not(.in)').forEach(function (el) {
          el.classList.add('in');
        });
      }, 1400);
    }

    /* ---- mobile drawer ---- */
    var drawer = document.querySelector('.drawer');
    var toggle = document.querySelector('.menu-toggle');
    if (drawer && toggle) {
      toggle.addEventListener('click', function (e) {
        e.preventDefault();
        drawer.classList.add('open');
      });
      drawer.addEventListener('click', function (e) {
        var t = e.target;
        if (t && (t.tagName === 'A' || t.tagName === 'BUTTON' ||
                  (t.closest && t.closest('.close')))) {
          drawer.classList.remove('open');
        }
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') drawer.classList.remove('open');
      });
    }

    /* ---- interactive anatomy ----
       Hover/focus on a list row or a pin isolates that component: the row
       goes .active, the pin goes .on, and the viz gets the pin's position
       in --sx/--sy so the spotlight crop lands on it. */
    var list = document.querySelector('.anatomy-list');
    var viz = document.querySelector('.anatomy-viz');
    if (list && viz) {
      var items = [].slice.call(list.querySelectorAll('.anatomy-item'));
      var pins = [].slice.call(viz.querySelectorAll('.an-pin'));

      var focus = function (step) {
        items.forEach(function (o) {
          o.classList.toggle('active', o.dataset.step === step);
        });
        pins.forEach(function (p) {
          p.classList.toggle('on', p.dataset.step === step);
        });
        var pin = pins.filter(function (p) { return p.dataset.step === step; })[0];
        if (pin) {
          viz.style.setProperty('--sx', pin.style.left);
          viz.style.setProperty('--sy', pin.style.top);
          viz.classList.add('focused');
        }
      };
      var clear = function () {
        items.forEach(function (o) { o.classList.remove('active'); });
        pins.forEach(function (p) { p.classList.remove('on'); });
        viz.classList.remove('focused');
      };

      items.concat(pins).forEach(function (el) {
        var enter = function () { focus(el.dataset.step); };
        el.addEventListener('mouseenter', enter);
        el.addEventListener('mouseleave', clear);
        el.addEventListener('focus', enter);
        el.addEventListener('blur', clear);
        /* touch: no hover, so tap to inspect */
        el.addEventListener('click', function () { focus(el.dataset.step); });
      });
    }

    /* ---- machining loop: play only in view, and only if a clip is set ---- */
    var vid = document.querySelector('.video-float video');
    if (vid && 'IntersectionObserver' in window &&
        matchMedia('(prefers-reduced-motion: no-preference)').matches) {
      var vio = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) { vid.play().catch(function () {}); }
          else { vid.pause(); }
        });
      }, { threshold: 0.3 });
      vio.observe(vid);
    }

    /* ---- fitment: transmission options depend on make, and the CTA builds a
           real collection URL from the selection ---- */
    var make = document.getElementById('make');
    var trans = document.getElementById('trans');
    var goal = document.getElementById('goal');
    var cta = document.querySelector('.fit-grid a.btn-primary');

    if (make && trans) {
      make.addEventListener('change', function () {
        var m = MAKES[make.value];
        var opts = (m && m.trans) || [];
        trans.innerHTML = '';
        var ph = document.createElement('option');
        ph.value = '';
        ph.textContent = 'Select Transmission';
        trans.appendChild(ph);
        opts.forEach(function (t) {
          var o = document.createElement('option');
          o.value = t.tag;              /* value carries the tag handle */
          o.textContent = t.label;      /* label stays human */
          trans.appendChild(o);
        });
        trans.value = '';
        retarget();
      });
    }

    /* Rewrite the CTA's href on every change. Doing it here rather than on
       click keeps the destination visible in the status bar on hover, and
       leaves the button a plain link — middle-click and "open in new tab"
       keep working. */
    function retarget() {
      if (!cta) return;
      var m = make && MAKES[make.value];
      var t = trans && trans.value;
      var g = goal && GOALS[goal.value];

      if (!m) { cta.setAttribute('href', FALLBACK); return; }
      if (!t) { cta.setAttribute('href', '/collections/' + m.handle); return; }

      if (g) {
        var ok = VALID[t] && VALID[t].indexOf(g) !== -1;
        if (!ok) { cta.setAttribute('href', ASK); return; }   /* would be empty */
        cta.setAttribute('href', '/collections/' + m.handle + '/' + t + '+' + TAG[g]);
        return;
      }
      cta.setAttribute('href', '/collections/' + m.handle + '/' + t);
    }

    [make, trans, goal].forEach(function (el) {
      if (el) el.addEventListener('change', retarget);
    });
    retarget();

    /* ---- gated "We'll spec it": #custom ships hidden (.spec-gate) and opens
       when the off-ramp pill — or any same-page #custom link, e.g. the hero's
       "Build a Custom Spec" — is clicked. The click's default anchor-scroll
       runs after this handler, so the section is visible by the time the
       browser scrolls to it. No-JS gets the section via the <noscript> style. */
    var specSec = document.getElementById('custom');
    var orCta = document.querySelector('.or-cta');
    if (specSec && specSec.classList.contains('spec-gate')) {
      if (orCta) {
        orCta.setAttribute('aria-expanded', 'false');
        orCta.setAttribute('aria-controls', 'custom');
      }
      var openSpec = function () {
        specSec.classList.remove('spec-gate');
        specSec.classList.add('spec-in');
        if (orCta) {
          orCta.classList.add('is-open');
          orCta.setAttribute('aria-expanded', 'true');
        }
      };
      document.addEventListener('click', function (e) {
        var t = e.target;
        if (t && t.closest && t.closest('a[href="#custom"]')) openSpec();
      });
      /* arriving on /#custom directly counts as intent: open, then land on it
         (the browser's own hash-scroll found a display:none section and gave up) */
      if (location.hash === '#custom') {
        openSpec();
        requestAnimationFrame(function () {
          specSec.scrollIntoView({ block: 'start' });
        });
      }
    }

    /* ---- global search overlay: Shopify predictive search, opened from the
       nav icon / drawer link / "/" or Cmd-K. Product thumbs reuse the
       dark-swap sprite sheets: the theme's cds-dark-swap.js is a loader whose
       src points at the current build; the build embeds SHEET, the 5x5 handle
       map and the cache stamp — fetched lazily on first open. ---- */
    var srch = document.getElementById('cds-search');
    if (srch) {
      var sIn = srch.querySelector('.srch-in');
      var sOut = srch.querySelector('.srch-out');
      var sQuick = srch.querySelector('.srch-quick');
      var sOpener = null, sTimer = null, sCtl = null, sSeq = 0;
      var dsSheet = '', dsVer = '', dsMap = null, dsTried = false;
      var lastData = null, lastQ = '';

      function loadDarkMap() {
        if (dsTried) return; dsTried = true;
        fetch(srch.getAttribute('data-darkswap'))
          .then(function (r) { return r.text(); })
          .then(function (t) {
            var m = t.match(/src\s*=\s*['"]([^'"]+)['"]/);
            if (!m) throw new Error('no build url');
            return fetch(m[1]);
          })
          .then(function (r) { return r.text(); })
          .then(function (t) {
            var sheet = t.match(/SHEET="([^"]+)"/);
            var map = t.match(/MAP=(\{[^}]*\})/);
            var ver = t.match(/\.jpg\?v=([A-Za-z0-9]+)"/);
            if (sheet && map) {
              dsSheet = sheet[1];
              dsVer = ver ? ver[1] : '';
              /* the minifier strips quotes from dash-free handle keys */
              dsMap = JSON.parse(map[1].replace(/([{,])([A-Za-z0-9_$]+):/g, '$1"$2":'));
              if (!srch.hidden && lastData) render(lastData, lastQ);
            }
          })
          .catch(function () { /* thumbs fall back to catalog images */ });
      }

      function esc(s) {
        var d = document.createElement('div');
        d.textContent = s == null ? '' : String(s);
        return d.innerHTML;
      }
      function money(p) {
        var n = parseFloat(String(p.price_min || p.price).replace(/,/g, ''));
        if (isNaN(n)) return '';
        var s = '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        var mx = parseFloat(String(p.price_max || '').replace(/,/g, ''));
        return (!isNaN(mx) && mx > n) ? 'From ' + s : s;
      }
      function thumb(p) {
        var code = dsMap && dsMap[p.handle];
        if (code) {
          return '<span class="srch-thumb" style="background-image:url(' + dsSheet + code[0] + '.jpg?v=' + dsVer +
                 ');background-size:500% 500%;background-position:' + (code[1] * 25) + '% ' + (code[2] * 25) + '%"></span>';
        }
        var img = (p.featured_image && p.featured_image.url) || p.image;
        if (!img) return '<span class="srch-thumb"></span>';
        img += (img.indexOf('?') > -1 ? '&' : '?') + 'width=120';
        return '<span class="srch-thumb"><img src="' + esc(img) + '" alt="" loading="lazy"></span>';
      }
      /* dealer-facing pages, the dealer blog, and internal staging/proof pages
         stay out of customer search (Brady, 2026-08-11) */
      var HIDE_LINKS = /^\/(pages\/(dealer-|cds-landing\b|dark-image-proof|thumb-framing-proof)|blogs\/dealer-updates\b)/;
      /* the Globo options app publishes helper "products" (option-set-*) that
         must never surface in customer search */
      function isRealProduct(p) {
        if (!p || (p.handle && p.handle.indexOf('option-set-') === 0)) return false;
        var tags = p.tags || [];
        for (var i = 0; i < tags.length; i++) {
          if (tags[i] === 'globo-product-options') return false;
        }
        return true;
      }

      function render(d, q) {
        lastData = d; lastQ = q;
        var res = (d && d.resources && d.resources.results) || {};
        var prods = (res.products || []).filter(isRealProduct).slice(0, 6);
        var links = [].concat(
          (res.collections || []).map(function (c) { return { u: c.url, t: c.title, k: 'Collection' }; }),
          (res.pages || []).map(function (pg) { return { u: pg.url, t: pg.title, k: 'Page' }; }),
          (res.articles || []).map(function (a) { return { u: a.url, t: a.title, k: 'Article' }; })
        ).filter(function (l) { return !HIDE_LINKS.test(l.u); }).slice(0, 7);
        var sugs = (res.queries || []).slice(0, 4);
        var h = '';
        if (sugs.length) {
          h += '<div class="srch-sugs">';
          sugs.forEach(function (s) {
            h += '<button type="button" class="srch-sg" data-q="' + esc(s.text) + '">' + esc(s.text) + '</button>';
          });
          h += '</div>';
        }
        if (prods.length || links.length) {
          h += '<div class="srch-grid">';
          h += '<div><span class="srch-eyebrow">Products</span><div class="srch-prods">';
          if (prods.length) {
            prods.forEach(function (p) {
              h += '<a class="srch-item" href="' + esc(p.url) + '">' + thumb(p) +
                   '<span class="srch-meta"><span class="srch-t">' + esc(p.title) + '</span>' +
                   '<span class="srch-p">' + money(p) + '</span></span></a>';
            });
          } else {
            h += '<div class="srch-empty">No matching products.</div>';
          }
          h += '</div></div>';
          h += '<div><span class="srch-eyebrow">On the site</span><div class="srch-links">';
          if (links.length) {
            links.forEach(function (l) {
              h += '<a class="srch-link" href="' + esc(l.u) + '"><b>' + esc(l.t) + '</b><i>' + l.k + '</i></a>';
            });
          } else {
            h += '<div class="srch-empty">Nothing else matched.</div>';
          }
          h += '</div></div></div>';
        } else {
          h += '<div class="srch-empty"><b>No matches for “' + esc(q) + '”</b>' +
               'Tell us your combination instead — every converter is spec’d to order. ' +
               '<a href="/pages/ask-circled" style="color:var(--ignite-bright)">Start a custom spec →</a></div>';
        }
        h += '<a class="srch-all" href="/search?q=' + encodeURIComponent(q) + '">All results for “' + esc(q) + '”' +
             '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6"></path></svg></a>';
        sQuick.hidden = true;
        sOut.innerHTML = h;
      }

      function doSearch(q) {
        if (sCtl) sCtl.abort();
        sCtl = window.AbortController ? new AbortController() : null;
        var seq = ++sSeq;
        srch.classList.add('is-loading');
        var url = '/search/suggest.json?q=' + encodeURIComponent(q) +
          '&resources[type]=product,collection,page,article,query' +
          '&resources[limit]=8&resources[limit_scope]=each' +
          '&resources[options][unavailable_products]=last&resources[options][prefix]=last';
        fetch(url, sCtl ? { signal: sCtl.signal } : undefined)
          .then(function (r) { return r.json(); })
          .then(function (d) {
            if (seq !== sSeq) return;
            srch.classList.remove('is-loading');
            render(d, q);
          })
          .catch(function (err) {
            if (err && err.name === 'AbortError') return;
            if (seq !== sSeq) return;
            srch.classList.remove('is-loading');
            sQuick.hidden = true;
            sOut.innerHTML = '<div class="srch-empty"><b>Live results unavailable</b></div>' +
              '<a class="srch-all" href="/search?q=' + encodeURIComponent(q) + '">Search the store for “' + esc(q) + '”' +
              '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6"></path></svg></a>';
          });
      }

      function sOpen(from) {
        sOpener = from || null;
        srch.hidden = false;
        document.documentElement.style.overflow = 'hidden';
        loadDarkMap();
        sIn.focus(); sIn.select();
        setTimeout(function () {
          if (!srch.hidden && document.activeElement !== sIn) sIn.focus();
        }, 60);
      }
      function sClose() {
        if (srch.hidden) return;
        srch.hidden = true;
        document.documentElement.style.overflow = '';
        sSeq++;
        if (sCtl) sCtl.abort();
        srch.classList.remove('is-loading');
        if (sOpener && sOpener.focus) sOpener.focus();
      }

      document.addEventListener('click', function (e) {
        var t = e.target;
        var trigger = t && t.closest && t.closest('[data-srch-open]');
        if (trigger) { e.preventDefault(); sOpen(trigger); }
      });
      srch.addEventListener('click', function (e) {
        var t = e.target;
        if (t && t.closest && t.closest('[data-srch-close]')) { e.preventDefault(); sClose(); }
      });
      sOut.addEventListener('click', function (e) {
        var t = e.target;
        var b = t && t.closest && t.closest('.srch-sg');
        if (b) { sIn.value = b.getAttribute('data-q'); sIn.focus(); doSearch(sIn.value.trim()); }
      });

      sIn.addEventListener('input', function () {
        var q = sIn.value.trim();
        clearTimeout(sTimer);
        if (q.length < 2) {
          sSeq++;
          if (sCtl) sCtl.abort();
          srch.classList.remove('is-loading');
          sOut.innerHTML = '';
          sQuick.hidden = false;
          lastData = null;
          return;
        }
        sTimer = setTimeout(function () { doSearch(q); }, 220);
      });

      document.addEventListener('keydown', function (e) {
        if (!srch.hidden) {
          if (e.key === 'Escape') { e.preventDefault(); sClose(); return; }
          if (e.key === 'Tab') {
            var f = srch.querySelectorAll('a[href],button:not([disabled]),input');
            if (f.length) {
              var first = f[0], last = f[f.length - 1];
              if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
              else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
            }
          }
          return;
        }
        var el = e.target, tag = ((el && el.tagName) || '').toLowerCase();
        if (tag === 'input' || tag === 'textarea' || tag === 'select' || (el && el.isContentEditable)) return;
        if (e.key === '/' || ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K'))) {
          e.preventDefault();
          sOpen(document.querySelector('[data-srch-open]'));
        }
      });
    }
  });
})();

/* Circle D — specialist contact sheet + anchor landing correction.

   "Talk to a Specialist" used to hard-link to /pages/contact. It now opens a
   three-way sheet: Front Chat, email, phone. The layouts differ on chat:
   layout/cds.liquid (homepage) already loads chat.bundle.js and inits Front
   with the default launcher, but page.cds-landing.liquid renders {% layout
   none %} so nothing Shopify- or app-injected exists there — the chat option
   lazy-loads the bundle on first click and inits against the same workspace.
   The anchors keep their /pages/contact href as the no-JS fallback. */
(function () {
  'use strict';

  var CHAT_ID = '853731d111309757a0889b123a4bc1ea'; /* must match layout/cds.liquid + theme.liquid */
  var FALLBACK = '/pages/contact';

  var modal = null, lastFocus = null, chatLoading = false;

  function icon(d) {
    return '<span class="spec-way-ic" aria-hidden="true"><svg viewBox="0 0 24 24">' + d + '</svg></span>';
  }
  function txt(t, d) {
    return '<span class="spec-way-tx"><span class="spec-way-t">' + t + '</span><span class="spec-way-d">' + d + '</span></span>' +
           '<svg class="spec-way-go" viewBox="0 0 24 24" aria-hidden="true"><path d="M9 6l6 6-6 6"></path></svg>';
  }

  function build() {
    var d = document.createElement('div');
    d.className = 'spec-modal';
    d.hidden = true;
    d.innerHTML =
      '<div class="spec-modal-bg" data-spec-close></div>' +
      '<div class="spec-modal-card" role="dialog" aria-modal="true" aria-labelledby="specSheetTitle">' +
        '<button type="button" class="spec-modal-x" data-spec-close aria-label="Close">' +
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"></path></svg>' +
        '</button>' +
        '<span class="spec-modal-k">Ask Circle D</span>' +
        '<h3 class="spec-modal-t" id="specSheetTitle">Talk to a specialist</h3>' +
                '<div class="spec-ways">' +
          '<button type="button" class="spec-way" data-spec-chat>' +
            icon('<path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path>') +
            txt('Live chat', 'Chat With Us Now') +
          '</button>' +
          '<a class="spec-way" href="mailto:sales@circledspecialties.com">' +
            icon('<path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><path d="M22 6l-10 7L2 6"></path>') +
            txt('Email us', 'sales@circledspecialties.com') +
          '</a>' +
          '<a class="spec-way" href="tel:+17138958834">' +
            icon('<path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z"></path>') +
            txt('Call us', '713-895-8834') +
          '</a>' +
        '</div>' +
      '</div>';
    document.body.appendChild(d);
    d.addEventListener('click', function (e) {
      if (e.target.closest('[data-spec-close]')) closeSheet();
    });
    d.querySelector('[data-spec-chat]').addEventListener('click', openChat);
    return d;
  }

  function openSheet() {
    modal = modal || build();
    lastFocus = document.activeElement;
    modal.hidden = false;
    document.body.classList.add('spec-modal-open');
    modal.querySelector('.spec-modal-x').focus();
  }
  function closeSheet() {
    if (!modal || modal.hidden) return;
    modal.hidden = true;
    document.body.classList.remove('spec-modal-open');
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  /* Front hides the widget entirely outside the workspace's online hours
     ("[settings] Widget hidden because current time is outside of online
     hours") and 'show' silently no-ops, so confirm the panel actually opened
     before closing the sheet. If it didn't, mark the row offline and leave
     email/phone standing. */
  function attemptShow(btn) {
    try { window.FrontChat('show'); } catch (_) { location.href = FALLBACK; return; }
    setTimeout(function () {
      btn.classList.remove('is-busy');
      chatLoading = false;
      var f = document.getElementById('front-chat-iframe');
      if (f && (f.offsetWidth > 0 || f.offsetHeight > 0)) { closeSheet(); return; }
      if (!f) { location.href = FALLBACK; return; }
      btn.classList.add('is-off');
      btn.querySelector('.spec-way-d').textContent = 'Offline Right Now — Email or Call Us';
    }, 700);
  }
  function openChat() {
    if (chatLoading) return;
    var btn = modal.querySelector('[data-spec-chat]');
    chatLoading = true;
    btn.classList.add('is-busy');
    if (window.FrontChat) { attemptShow(btn); return; }
    var s = document.createElement('script');
    s.src = 'https://chat-assets.frontapp.com/v1/chat.bundle.js';
    s.onload = function () {
      var done = false;
      var once = function () { if (!done) { done = true; attemptShow(btn); } };
      try {
        window.FrontChat('init', { chatId: CHAT_ID, useDefaultLauncher: false, onInitCompleted: once });
        /* some bundle builds never fire the callback — don't strand the sheet */
        setTimeout(once, 2500);
      } catch (_) { location.href = FALLBACK; }
    };
    s.onerror = function () { location.href = FALLBACK; };
    document.head.appendChild(s);
  }

  document.addEventListener('click', function (e) {
    var t = e.target.closest && e.target.closest('[data-specialist]');
    if (!t) return;
    e.preventDefault();
    openSheet();
  });
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && modal && !modal.hidden) closeSheet();
  });

  /* ---- anchor landing correction --------------------------------------
     scroll-margin-top (see cds-landing.css) keeps hash targets clear of the
     fixed nav, but iOS still drifts when media above the target finishes
     loading mid-flight through the long smooth scroll. Re-square once the
     scroll settles. #custom stays excluded — the spec-gate handler owns it
     (the section is display:none until that code reveals it). */
  function navClearance(el) {
    return parseFloat(getComputedStyle(el).scrollMarginTop) || 0;
  }
  function settle(el, clearance) {
    var t0 = Date.now(), lastY = null, still = 0;
    (function tick() {
      var y = window.pageYOffset;
      if (lastY !== null && Math.abs(y - lastY) < 2) still++; else still = 0;
      lastY = y;
      if (still >= 3 || Date.now() - t0 > 1800) {
        /* Batch the layout read + scroll write into one frame so we never read
           geometry immediately after a style-invalidating write (forced reflow).
           clearance (scroll-margin-top) is static, so it was read once up front. */
        requestAnimationFrame(function () {
          var diff = el.getBoundingClientRect().top - clearance;
          if (Math.abs(diff) > 8) {
            var html = document.documentElement, prev = html.style.scrollBehavior;
            html.style.scrollBehavior = 'auto';
            window.scrollTo(0, window.pageYOffset + diff);
            html.style.scrollBehavior = prev;
          }
        });
        return;
      }
      setTimeout(tick, 120);
    })();
  }
  document.addEventListener('click', function (e) {
    var a = e.target.closest && e.target.closest('a[href^="#"]');
    if (!a) return;
    var id = (a.getAttribute('href') || '').slice(1);
    if (!id || id === 'custom') return;
    var el = document.getElementById(id);
    if (!el) return;
    e.preventDefault();
    if (history.pushState) history.pushState(null, '', '#' + id);
    var clearance = navClearance(el); /* read style once, before smooth scroll invalidates layout */
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    settle(el, clearance);
  });
})();
